"use client";

import { Format, getFormatDisplayName } from '../../lib/formats';
import { CodeBlock, CodeBlockLine, SupernovaCodeBlock } from '@repo/ui';
import { ContentContainer } from './content-container';
import { cn } from '@repo/ui/lib/utils';

interface PandasMergeProps {
  format: Format;
}

function PandasMerge({ format }: PandasMergeProps) {
  return (
    <div className="mt-6 space-y-4">
      <p>First, let's install pandas if you haven't already:</p>
      <CodeBlock language="bash" theme="supernova">
{`pip install pandas`}
      </CodeBlock>
      
      <p>{`Now we can load your ${format} files into dataframes:`}</p>
      <CodeBlock language="python" theme="supernova">
{`import pandas as pd`}
      </CodeBlock>
      
      <p>Let's load your first file:</p>
      <CodeBlock language="python" theme="supernova">
{format === Format.CSV ? 
`df1 = pd.read_csv('path/to/file1.csv')` :
format === Format.TSV ? 
`df1 = pd.read_csv('path/to/file1.tsv', sep='\\t')` :
format === Format.JSON ? 
`df1 = pd.read_json('path/to/file1.json')` :
format === Format.PARQUET ? 
`df1 = pd.read_parquet('path/to/file1.parquet')` :
`df1 = pd.read_excel('path/to/file1.xlsx')`}
      </CodeBlock>
      
      <p>And your second file:</p>
      <CodeBlock language="python" theme="supernova">
{format === Format.CSV ? 
`df2 = pd.read_csv('path/to/file2.csv')` :
format === Format.TSV ? 
`df2 = pd.read_csv('path/to/file2.tsv', sep='\\t')` :
format === Format.JSON ? 
`df2 = pd.read_json('path/to/file2.json')` :
format === Format.PARQUET ? 
`df2 = pd.read_parquet('path/to/file2.parquet')` :
`df2 = pd.read_excel('path/to/file2.xlsx')`}
      </CodeBlock>
      
      <p>{`Great! Now we can merge the dataframes using the concat function:`}</p>
      <CodeBlock language="python" theme="supernova">
{`merged_df = pd.concat([df1, df2], ignore_index=True)`}
      </CodeBlock>
      
      <p>{`Finally, let's save your newly merged data to a file:`}</p>
      <CodeBlock language="python" theme="supernova">
{format === Format.CSV ? 
`merged_df.to_csv('path/to/merged_file.csv', index=False)` :
format === Format.TSV ? 
`merged_df.to_csv('path/to/merged_file.tsv', sep='\\t', index=False)` :
format === Format.JSON ? 
`merged_df.to_json('path/to/merged_file.json', orient='records')` :
format === Format.PARQUET ? 
`merged_df.to_parquet('path/to/merged_file.parquet', index=False)` :
`merged_df.to_excel('path/to/merged_file.xlsx', index=False)`}
      </CodeBlock>
      
      <p>Need to merge more than two files? No problem! Just add them to the list in the concat function:</p>
      <CodeBlock language="python" theme="supernova">
{`merged_df = pd.concat([df1, df2, df3, df4], ignore_index=True)`}
      </CodeBlock>
    </div>
  );
}

interface DuckDBMergeProps {
  format: Format;
}

function DuckDBMerge({ format }: DuckDBMergeProps) {
  return (
    <div className="mt-6 space-y-4">
      <p>Let's start by installing DuckDB for Python:</p>
      <CodeBlock language="bash" theme="supernova">
{`pip install duckdb`}
      </CodeBlock>
      
      <p>Now we'll import the library and create a connection:</p>
      <CodeBlock language="python" theme="supernova">
{`import duckdb
con = duckdb.connect()`}
      </CodeBlock>
      
      <p>{`Here's a simple DuckDB query that will merge your ${format} files using UNION ALL:`}</p>
      <CodeBlock language="python" theme="supernova">
{`query = """
COPY (
  SELECT * FROM 'path/to/file1.${format.toLowerCase()}'
  UNION ALL
  SELECT * FROM 'path/to/file2.${format.toLowerCase()}'
) TO 'path/to/merged_file.${format.toLowerCase()}' (${format === Format.CSV ? "HEADER, " : ""}FORMAT '${format.toLowerCase()}')
"""`}
      </CodeBlock>
      
      <p>Just run this query to perform the merge:</p>
      <CodeBlock language="python" theme="supernova">
{`con.execute(query)`}
      </CodeBlock>
      
      <p>Got more than two files? Simply add more UNION ALL statements like this:</p>
      <CodeBlock language="python" theme="supernova">
{`query = """
COPY (
  SELECT * FROM 'path/to/file1.${format.toLowerCase()}'
  UNION ALL
  SELECT * FROM 'path/to/file2.${format.toLowerCase()}'
  UNION ALL
  SELECT * FROM 'path/to/file3.${format.toLowerCase()}'
) TO 'path/to/merged_file.${format.toLowerCase()}' (${format === Format.CSV ? "HEADER, " : ""}FORMAT '${format.toLowerCase()}')
"""`}
      </CodeBlock>
      
      <p>What's great about DuckDB is that it's incredibly efficient for large files - it processes data in a columnar format and can handle files that don't fit in memory. Perfect for those bigger merging jobs!</p>
    </div>
  );
}

interface ClickHouseMergeProps {
  format: Format;
}

function ClickHouseMerge({ format }: ClickHouseMergeProps) {
  return (
    <div className="mt-6 space-y-4">
      <p>Let's begin by installing the ClickHouse Connect library for Python:</p>
      <CodeBlock language="bash" theme="supernova">
{`pip install clickhouse-connect`}
      </CodeBlock>
      
      <p>Now we'll import the library and create a client connection:</p>
      <CodeBlock language="python" theme="supernova">
{`import clickhouse_connect
client = clickhouse_connect.get_client(host='localhost')`}
      </CodeBlock>
      
      <p>Here's how you can merge your files using a single UNION ALL query:</p>
      <CodeBlock language="python" theme="supernova">
{`query = f"""
CREATE TABLE merged_file AS
  SELECT * FROM file('path/to/file1.${format.toLowerCase()}', ${format.toLowerCase()})
  UNION ALL
  SELECT * FROM file('path/to/file2.${format.toLowerCase()}', ${format.toLowerCase()})
"""
client.command(query)`}
      </CodeBlock>
      
      <p>Then export your merged data to a file:</p>
      <CodeBlock language="python" theme="supernova">
{`export_query = f"""
SELECT * FROM merged_file 
INTO OUTFILE 'path/to/merged_file.${format.toLowerCase()}'
FORMAT ${format.toLowerCase()}
"""
client.command(export_query)`}
      </CodeBlock>
      
      <p>Need to merge more than two files? Just add more UNION ALL statements like this:</p>
      <CodeBlock language="python" theme="supernova">
{`query = f"""
CREATE TABLE merged_file AS
  SELECT * FROM file('path/to/file1.${format.toLowerCase()}', ${format.toLowerCase()})
  UNION ALL
  SELECT * FROM file('path/to/file2.${format.toLowerCase()}', ${format.toLowerCase()})
  UNION ALL
  SELECT * FROM file('path/to/file3.${format.toLowerCase()}', ${format.toLowerCase()})
"""
client.command(query)`}
      </CodeBlock>
      
      <p>Want to skip the intermediate table? You can merge directly to a file in one step:</p>
      <CodeBlock language="python" theme="supernova">
{`direct_query = f"""
SELECT * FROM file('path/to/file1.${format.toLowerCase()}', ${format.toLowerCase()})
UNION ALL
SELECT * FROM file('path/to/file2.${format.toLowerCase()}', ${format.toLowerCase()})
INTO OUTFILE 'path/to/merged_file.${format.toLowerCase()}'
FORMAT ${format.toLowerCase()}
"""
client.command(direct_query)`}
      </CodeBlock>
      
      <p>ClickHouse really shines when you're working with massive datasets - it's a powerful columnar database that processes large volumes of data lightning fast, making it perfect for merging even the largest files.</p>
    </div>
  );
}

interface HowToMergeProps {
  format: Format;
  className?: string;
}

export function HowToMerge({ format, className }: HowToMergeProps) {
  return (
    <ContentContainer className={cn("space-y-6", className)}>
      <h2 className="text-2xl font-bold mb-4">{`How to merge ${getFormatDisplayName(format)} files in Python`}</h2>
      
      <p>
        Here are three effective ways to merge multiple {getFormatDisplayName(format)} files in Python using different libraries. Each approach has its own advantages depending on your specific needs and file sizes.
      </p>
      
      <h3>{`Merging ${getFormatDisplayName(format)} files with Pandas`}</h3>
      <p>Pandas provides a straightforward approach for merging files and works well for most common data tasks:</p>
      <PandasMerge format={format} />
      
      <h3>{`Merging ${getFormatDisplayName(format)} files with DuckDB`}</h3>
      <p>DuckDB is an in-process SQL OLAP database that's perfect for larger files and analytical workloads:</p>
      <DuckDBMerge format={format} />
      
      <h3>{`Merging ${getFormatDisplayName(format)} files with ClickHouse`}</h3>
      <p>ClickHouse is a high-performance column-oriented database system that's excellent for large-scale data processing:</p>
      <ClickHouseMerge format={format} />
    </ContentContainer>
  );
}
